import React, { useState, useEffect, useRef, useCallback } from 'react';
import MathHtml from '../../components/shared/MathHtml';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getDailyPaper, saveDailyPaperAnswer, submitDailyPaper } from '../../api';

// Daily Paper — taking / resuming.
// No timer is shown: this paper is untimed. Every answer is written to the server as
// soon as it is picked, so closing the browser loses nothing and reopening the page
// restores exactly where he left off.

function toHtml(s) { const t = s || ''; return t.indexOf('<') >= 0 ? t : t.split('\n').join('<br>'); }
function HtmlDiv(p) { return React.createElement(MathHtml, { tag: 'div', style: p.style, html: toHtml(p.html) }); }
function HtmlSpan(p) { return React.createElement(MathHtml, { tag: 'span', style: p.style, html: toHtml(p.html) }); }

const OPTS = ['A', 'B', 'C', 'D'];
const SAVE_DEBOUNCE_MS = 400;

export default function DailyPaperTest() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [paper, setPaper] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [answers, setAnswers] = useState({});        // question_id -> 'A'|'B'|'C'|'D'
  const [current, setCurrent] = useState(0);
  const [showPanel, setShowPanel] = useState(true);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const [failedCount, setFailedCount] = useState(0);  // answers whose last save failed
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [shortNotice] = useState(location.state?.shortNotice || '');

  // --- refs used by the save/timer machinery -------------------------------
  // Refs (not state) because these are read inside timeouts and event handlers
  // that would otherwise close over stale values.
  const answersRef = useRef({});          // mirror of `answers`, always current
  const timeRef = useRef({});             // question_id -> cumulative seconds already banked
  const clockRef = useRef({ qid: null, since: null }); // since === null means the clock is paused
  const debounceRef = useRef({});         // question_id -> { qid, timer } for the pending save
  const failedRef = useRef(new Map());    // question_id -> qid, for saves whose last PATCH failed
  const inFlightRef = useRef(0);          // number of PATCHes currently in the air
  const aliveRef = useRef(true);          // false after unmount, so we don't setState on a dead component

  // --- per-question timing -------------------------------------------------
  // The student never sees this. `bank()` moves however many whole seconds have
  // elapsed on the visible question into timeRef and restarts the stopwatch, so the
  // figure sent with each save is the cumulative time spent on that one question.
  const bank = useCallback(() => {
    const c = clockRef.current;
    if (c.qid == null || c.since == null) return;
    const whole = Math.floor((Date.now() - c.since) / 1000);
    if (whole > 0) {
      timeRef.current[c.qid] = (timeRef.current[c.qid] || 0) + whole;
      // Advance by exactly the seconds banked so the sub-second remainder isn't lost.
      c.since += whole * 1000;
    }
  }, []);

  // Start (or restart) the stopwatch on a question. Stays paused if the tab is hidden.
  const startClock = useCallback((qid) => {
    clockRef.current = { qid, since: document.hidden ? null : Date.now() };
  }, []);

  // An idle tab must not inflate the figure: bank what has elapsed and stop counting
  // while hidden, then resume counting when the student comes back.
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) {
        bank();
        clockRef.current.since = null;
      } else if (clockRef.current.qid != null) {
        clockRef.current.since = Date.now();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [bank]);

  useEffect(() => () => { aliveRef.current = false; bank(); }, [bank]);

  // --- load / resume -------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    setLoading(true); setLoadError('');
    getDailyPaper(testId)
      .then(r => {
        if (cancelled) return;
        const d = r.data;
        if (d.status !== 'in_progress') {
          // Already submitted (e.g. the student reopened an old link) — send them to the result.
          navigate(`/student/daily-paper/${testId}/result`, { replace: true });
          return;
        }
        // RESUME: every answer already stored on the server comes back into local state,
        // and the seconds already spent on each question carry on from where they stopped.
        const restored = {};
        const times = {};
        (d.questions || []).forEach(q => {
          if (q.student_answer) restored[q.question_id] = q.student_answer;
          times[q.question_id] = q.time_spent_seconds || 0;
        });
        answersRef.current = restored;
        timeRef.current = times;
        setAnswers(restored);
        setPaper(d);
        // Land on the first unanswered question so resuming picks up where he stopped.
        const firstUnanswered = (d.questions || []).findIndex(q => !q.student_answer);
        const startAt = firstUnanswered === -1 ? 0 : firstUnanswered;
        setCurrent(startAt);
        const firstQ = (d.questions || [])[startAt];
        if (firstQ) startClock(firstQ.question_id);
        setLoading(false);
      })
      .catch(e => {
        if (cancelled) return;
        setLoadError(e.response?.data?.detail || 'Could not open this paper. Check your connection and refresh the page.');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [testId, navigate, startClock]);

  // --- saving --------------------------------------------------------------
  // One PATCH per question carrying the current answer and the cumulative seconds.
  // Failures are remembered in failedRef and retried on the next interaction, on
  // navigation, and before submit — an answer is never dropped without the student seeing it.
  const saveNow = useCallback(async (qid) => {
    const key = String(qid);
    if (debounceRef.current[key]) {
      clearTimeout(debounceRef.current[key].timer);
      delete debounceRef.current[key];
    }
    bank(); // make sure the on-screen question's seconds are current before we send them
    const payload = {
      question_id: qid,
      answer: answersRef.current[qid] || null,   // null clears the answer server-side
      time_spent_seconds: timeRef.current[qid] || 0,
    };
    inFlightRef.current += 1;
    if (aliveRef.current) setSaveState('saving');
    try {
      await saveDailyPaperAnswer(testId, payload);
      failedRef.current.delete(key);
      return true;
    } catch (e) {
      failedRef.current.set(key, qid);
      return false;
    } finally {
      inFlightRef.current -= 1;
      if (aliveRef.current) {
        setFailedCount(failedRef.current.size);
        const pending = inFlightRef.current > 0 || Object.keys(debounceRef.current).length > 0;
        if (pending) setSaveState('saving');
        else setSaveState(failedRef.current.size > 0 ? 'error' : 'saved');
      }
    }
  }, [testId, bank]);

  const scheduleSave = useCallback((qid) => {
    const key = String(qid);
    if (debounceRef.current[key]) clearTimeout(debounceRef.current[key].timer);
    setSaveState('saving');
    debounceRef.current[key] = { qid, timer: setTimeout(() => { saveNow(qid); }, SAVE_DEBOUNCE_MS) };
  }, [saveNow]);

  // Retry everything that failed earlier. Called on every interaction and navigation.
  const retryFailed = useCallback(async (exceptQid) => {
    const ids = Array.from(failedRef.current.values()).filter(id => id !== exceptQid);
    for (const id of ids) await saveNow(id);
  }, [saveNow]);

  // Send anything still queued or failed, and wait for it. Used on navigation and submit.
  const flushAll = useCallback(async () => {
    const byKey = new Map();
    Object.entries(debounceRef.current).forEach(([k, v]) => byKey.set(k, v.qid));
    failedRef.current.forEach((qid, k) => byKey.set(k, qid));
    for (const qid of byKey.values()) await saveNow(qid);
  }, [saveNow]);

  const choose = useCallback((qid, letter) => {
    const next = { ...answersRef.current, [qid]: letter };
    answersRef.current = next;
    setAnswers(next);
    scheduleSave(qid);
    retryFailed(qid);   // a fresh interaction is a good moment to retry earlier failures
  }, [scheduleSave, retryFailed]);

  const clearAnswer = useCallback((qid) => {
    const next = { ...answersRef.current };
    delete next[qid];
    answersRef.current = next;
    setAnswers(next);
    scheduleSave(qid);  // sends answer: null
    retryFailed(qid);
  }, [scheduleSave, retryFailed]);

  // Moving between questions banks the time on the one being left, flushes its save,
  // and restarts the stopwatch on the new one.
  const goTo = useCallback((index) => {
    if (!paper) return;
    const qs = paper.questions || [];
    if (index < 0 || index >= qs.length) return;
    bank();
    flushAll();                       // fire-and-forget; failures stay in failedRef and retry later
    setCurrent(index);
    startClock(qs[index].question_id);
    window.scrollTo(0, 0);
  }, [paper, bank, flushAll, startClock]);

  const doSubmit = useCallback(async () => {
    setSubmitting(true); setSubmitError('');
    bank();
    try {
      await flushAll();               // nothing may be left unsaved when we submit
      if (failedRef.current.size > 0) {
        setSubmitError(`${failedRef.current.size} answer(s) could not be saved. Check your internet connection and press Submit again — nothing has been lost.`);
        setSubmitting(false);
        return;
      }
      const r = await submitDailyPaper(testId);
      navigate(`/student/daily-paper/${testId}/result`, { replace: true, state: { result: r.data } });
    } catch (e) {
      setSubmitError(e.response?.data?.detail || 'Could not submit the paper. Check your connection and press Submit again.');
      setSubmitting(false);
    }
  }, [testId, navigate, bank, flushAll]);

  // --- render --------------------------------------------------------------
  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}><div className="spinner" /></div>;
  if (loadError) {
    return (
      <div className="card" style={{ borderColor: 'var(--danger)', maxWidth: 620, margin: '40px auto' }}>
        <div style={{ color: 'var(--danger)', fontWeight: 600, marginBottom: 6 }}>{loadError}</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14 }}>
          Your saved answers are safe on the server. Reloading will bring them back.
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-primary" onClick={() => window.location.reload()}>Reload this paper</button>
          <button className="btn btn-ghost" onClick={() => navigate('/student/daily-paper')}>Back to Daily Test</button>
        </div>
      </div>
    );
  }
  if (!paper) return null;

  const qs = paper.questions || [];
  const q = qs[current];
  const total = qs.length;
  const attempted = Object.keys(answers).length;
  const notAttempted = total - attempted;

  const saveLabel = saveState === 'saving' ? 'Saving…'
    : saveState === 'error' ? 'Not saved'
      : saveState === 'saved' ? 'Saved' : '';
  const saveColour = saveState === 'error' ? '#FCA5A5' : '#A5B4FC';

  return (
    <div>
      {/* Sticky paper header — deliberately has no countdown: this paper is untimed. */}
      <div style={{
        background: '#1E1B4B', color: 'white', padding: '10px 20px', borderRadius: 12,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        gap: 12, flexWrap: 'wrap', position: 'sticky', top: 68, zIndex: 90, marginBottom: 18,
      }}>
        <div>
          <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 15 }}>{paper.title || 'Daily Test'}</div>
          <div style={{ fontSize: 12, opacity: 0.75 }}>
            {attempted} answered · {notAttempted} remaining · no time limit
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {saveLabel && (
            <span style={{ fontSize: 12, color: saveColour, minWidth: 62, textAlign: 'right' }}>{saveLabel}</span>
          )}
          <button onClick={() => setShowPanel(!showPanel)}
            style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.3)', background: 'transparent', color: 'white', fontSize: 13 }}>
            Questions
          </button>
          <button onClick={() => setConfirming(true)} disabled={submitting}
            style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#10B981', color: 'white', fontWeight: 700, fontSize: 14 }}>
            {submitting ? 'Submitting…' : 'Submit paper'}
          </button>
        </div>
      </div>

      {shortNotice && (
        <div style={{ background: '#FEF3C7', border: '1px solid #F59E0B', color: '#92400E', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 13 }}>
          {shortNotice} That is everything available from the concepts logged so far — the paper is still worth doing.
        </div>
      )}

      {/* Repeated save failures get a loud banner: he must not finish a paper thinking it saved. */}
      {failedCount > 0 && (
        <div style={{ background: '#FEE2E2', border: '1px solid #FCA5A5', color: '#991B1B', borderRadius: 10, padding: '12px 14px', marginBottom: 14, fontSize: 13 }}>
          <strong>{failedCount} answer{failedCount === 1 ? '' : 's'} could not be saved.</strong>{' '}
          Your internet connection looks unstable. Nothing is lost — keep the page open, and
          press the button below (or answer another question) to try again.
          <div style={{ marginTop: 10 }}>
            <button className="btn btn-sm" style={{ background: '#991B1B', color: 'white' }}
              onClick={() => retryFailed(null)}>Retry saving now</button>
          </div>
        </div>
      )}

      {submitError && (
        <div style={{ background: '#FEE2E2', border: '1px solid #FCA5A5', color: '#991B1B', borderRadius: 10, padding: '12px 14px', marginBottom: 14, fontSize: 13 }}>
          {submitError}
        </div>
      )}

      {/* Submit confirmation — states plainly how many are unanswered. */}
      {confirming && (
        <div className="card" style={{ marginBottom: 14, borderTop: '3px solid var(--warning)' }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Submit this paper?</div>
          <div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 14 }}>
            {notAttempted === 0
              ? 'All 45 questions are answered. Once you submit you cannot change your answers.'
              : `${notAttempted} of ${total} question${notAttempted === 1 ? ' is' : 's are'} still unanswered and will be marked as skipped. Once you submit you cannot change your answers.`}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-primary" disabled={submitting} onClick={doSubmit}>
              {submitting ? 'Submitting…' : 'Yes, submit and see my result'}
            </button>
            <button className="btn btn-ghost" disabled={submitting} onClick={() => setConfirming(false)}>
              Keep working on it
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Question {current + 1} of {total}</span>
            <div style={{ flex: 1, height: 4, background: 'var(--border)', borderRadius: 2 }}>
              <div style={{ height: '100%', borderRadius: 2, background: 'var(--primary)', width: `${((current + 1) / total) * 100}%`, transition: 'width 0.3s' }} />
            </div>
          </div>

          <div className="card">
            {q.passage_text && (
              <div style={{ background: '#F8FAFC', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', marginBottom: 20, fontSize: 14, lineHeight: 1.9 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8 }}>Passage</div>
                <HtmlDiv html={q.passage_text} />
              </div>
            )}

            <HtmlDiv html={q.question_text} style={{ fontSize: 16, lineHeight: 1.8, marginBottom: 20, fontWeight: 500 }} />
            {q.question_image_url && <img src={q.question_image_url} alt="" style={{ maxWidth: '100%', borderRadius: 8, marginBottom: 16, border: '1px solid var(--border)' }} />}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {OPTS.map(opt => {
                const val = q[`option_${opt.toLowerCase()}`];
                const img = q[`option_${opt.toLowerCase()}_image_url`];
                if (val == null && !img) return null;
                const selected = answers[q.question_id] === opt;
                return (
                  <button key={opt} onClick={() => choose(q.question_id, opt)}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderRadius: 10, width: '100%', border: `2px solid ${selected ? '#4F46E5' : '#E2E8F0'}`, background: selected ? '#EEF2FF' : 'white', textAlign: 'left', transition: 'all 0.12s' }}>
                    <span style={{ width: 30, height: 30, borderRadius: 8, flexShrink: 0, background: selected ? '#4F46E5' : '#F1F5F9', color: selected ? 'white' : '#64748B', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13 }}>{opt}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <HtmlSpan html={val} style={{ fontSize: 15, color: selected ? '#3730A3' : '#0F172A', fontWeight: selected ? 500 : 400 }} />
                      {img && <img src={img} alt="" style={{ maxWidth: '100%', borderRadius: 6, marginTop: 6 }} />}
                    </div>
                  </button>
                );
              })}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, gap: 10, flexWrap: 'wrap' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => clearAnswer(q.question_id)}
                style={{ visibility: answers[q.question_id] ? 'visible' : 'hidden' }}>
                Clear answer
              </button>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-ghost" disabled={current === 0} onClick={() => goTo(current - 1)}>Prev</button>
                <button className="btn btn-primary" disabled={current === total - 1} onClick={() => goTo(current + 1)}>Next</button>
              </div>
            </div>
          </div>

          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12 }}>
            No timer on this paper. Every answer is saved to the server the moment you pick it —
            you can close the browser and carry on later from where you stopped.
          </div>
        </div>

        {showPanel && (
          <div style={{ width: 220, flexShrink: 0 }} className="hide-mobile">
            <div className="card" style={{ padding: 16, position: 'sticky', top: 150 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Question Map</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 4 }}>
                {qs.map((qq, i) => (
                  <button key={qq.question_id} onClick={() => goTo(i)}
                    style={{
                      width: 34, height: 34, borderRadius: 6,
                      border: current === i ? '2px solid #4F46E5' : '1px solid #E2E8F0',
                      background: answers[qq.question_id] ? '#D1FAE5' : current === i ? '#EEF2FF' : '#F8FAFC',
                      color: current === i ? '#4F46E5' : '#334155',
                      fontWeight: current === i ? 700 : 400, fontSize: 12,
                    }}>{i + 1}</button>
                ))}
              </div>
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)', fontSize: 13 }}>
                <div>Answered: <strong>{attempted}</strong></div>
                <div>Remaining: <strong>{notAttempted}</strong></div>
              </div>
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)', fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.6 }}>
                <div><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: '#D1FAE5', border: '1px solid #6EE7B7', marginRight: 6 }} />Answered</div>
                <div><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: '#F8FAFC', border: '1px solid #E2E8F0', marginRight: 6 }} />Not answered</div>
                <div><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: '#EEF2FF', border: '2px solid #4F46E5', marginRight: 6 }} />Where you are</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
